import {
  ENTRY_FEE_PGA_CANADA_S5,
  FIRST_PLACE_PRIZE_PGA_CANADA_S5,
  INITIAL_PURSE_PGA_CANADA_S5,
  MIN_PRIZE_PGA_CANADA_S5,
  PRIZE_SCALING_PGA_CANADA_S5,
  SIDE_GAMES_PGA_CANADA_S5,
  SPONSOR_PURSE_PGA_CANADA_S5,
} from "@/utils/constants";

const paidPlacesFor = (playersCount: number) =>
  Math.max(1, Math.trunc(playersCount * 0.1));

const poolFor = (playersCount: number) =>
  INITIAL_PURSE_PGA_CANADA_S5 +
  SPONSOR_PURSE_PGA_CANADA_S5 +
  ENTRY_FEE_PGA_CANADA_S5 * playersCount -
  SIDE_GAMES_PGA_CANADA_S5;

/** Weight of a rank below the winner: decaying scale x reverse rank. */
const weightFor = (rank: number, paidPlaces: number) =>
  Math.max(1, 10 * Math.pow(1 - PRIZE_SCALING_PGA_CANADA_S5, rank - 2)) *
  (paidPlaces - rank + 1);

/**
 * PGA of Canada, season 5 (NEXT Qualifier prize distribution workbook).
 *
 *   purse    = Trackman + sponsor + players x fee
 *   pool     = purse - side games
 *   M        = trunc(players / 10) paid places, at least 1
 *   rank 1   = fixed FIRST_PLACE_PRIZE
 *   scale(j) = max(1, 10 x (1 - scaling)^(j - 2))
 *   weight(j)= scale(j) x (M - j + 1)      for j >= 2
 *
 * Ranks 3..M take their weighted share of (pool - first place), floored at
 * MIN_PRIZE. Second place settles against those unrounded amounts. Round only
 * the completed table for display, matching the workbook's whole-dollar
 * formatting. With fewer than two paid places the winner takes the pool.
 *
 * Kept separate from the Australia calculator on purpose: first place and
 * scaling differ, and a change for one must never move the other's money.
 *
 * @returns Prize per rank, index 0 = 1st, length playersCount, unpaid = 0.
 */
export const prizeCalculatorPGA_CA_S5 = (playersCount: number): number[] => {
  if (!playersCount || playersCount <= 0) return [];

  const pool = poolFor(playersCount);
  const prizes = new Array<number>(playersCount).fill(0);
  if (pool <= 0) return prizes;

  const paidPlaces = Math.min(playersCount, paidPlacesFor(playersCount));

  // Too few players for a curve: the winner takes the pool.
  if (paidPlaces < 2) {
    prizes[0] = pool;
    return prizes;
  }

  const firstPlace = Math.min(FIRST_PLACE_PRIZE_PGA_CANADA_S5, pool);
  const remainingPool = pool - firstPlace;
  prizes[0] = firstPlace;

  let totalWeight = 0;
  for (let rank = 2; rank <= paidPlaces; rank++) {
    totalWeight += weightFor(rank, paidPlaces);
  }

  let awarded = 0;
  for (let rank = 3; rank <= paidPlaces; rank++) {
    const prize = Math.max(
      MIN_PRIZE_PGA_CANADA_S5,
      (weightFor(rank, paidPlaces) / totalWeight) * remainingPool,
    );
    prizes[rank - 1] = prize;
    awarded += prize;
  }

  prizes[1] = Math.max(MIN_PRIZE_PGA_CANADA_S5, remainingPool - awarded);

  return prizes.map(Math.round);
};
